import BreadCrumbs from 'components/breadcrumbs'
import CustomButton from 'components/Buttons/CustomButton'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Form, useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardText,
  Container,
  FormGroup,
  Input,
  Label,
} from 'reactstrap'
import {
  useAddEventMutation,
  useEditEventMutation,
  useFetchEventForFormQuery,
  useFetchUtilsEventQuery,
} from './eventApiSlice'
import { URL_EVENT, URL_EVENT_TIMELINE } from 'utils/pathUrl'
import { Calendar, X } from 'react-feather'
import PMBSelect from 'components/PMBSelect'
import Flatpickr from 'react-flatpickr'
import { Indonesian } from 'flatpickr/dist/l10n/id'
import moment from 'moment-timezone'
import usePartialUpload from 'hooks/usePartialUpload'
import LoadingAnimation from 'components/LoadingAnimation'

const FormEvent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const location = useLocation()
  const dataState = location.state
  const queryParams = new URLSearchParams(location.search)
  const userId = queryParams.get('userId')

  const [payload, setPayload] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [fileNames, setFileNames] = useState([])

  const [addEvent, resAddEvent] = useAddEventMutation()
  const [updateEvent, resUpdateEvent] = useEditEventMutation()
  const { uploadFile } = usePartialUpload()

  const resUtils = useFetchUtilsEventQuery()
  const resEvent = useFetchEventForFormQuery()

  const handleSubmit = async () => {
    try {
      let body = { ...payload }

      if (typeof payload?.verified === 'boolean' || !payload?.verified) {
        body.verified = payload.verified ? 1 : 0
      }

      const dateFields = ['eventDate', 'expiryDate']

      dateFields.forEach((field) => {
        const value = payload?.[field]

        if (Array.isArray(value) && value.length > 0) {
          body[field] = moment(value[0]).utc().toISOString()
        } else if (value && typeof value !== 'string') {
          body[field] = moment(value).utc().toISOString()
        }
      })

      if (Array.isArray(payload.attachments)) {
        body.attachments = payload.attachments
          .flat(Infinity)
          .map((a) => a?.fileUrl)
          .filter(Boolean)
      }

      const result = dataState ? await updateEvent(body) : await addEvent(body)

      if (result?.error) {
        throw result
      } else {
        navigate(userId ? `${URL_EVENT}/userId=${userId}` : URL_EVENT)
        enqueueSnackbar(`Event berhasil ${dataState ? 'diubah' : 'ditambah'}`, {
          variant: 'success',
        })
      }
    } catch (error) {
      enqueueSnackbar(
        error?.error?.data?.message ||
          error?.error?.message ||
          error?.error?.originalStatus ||
          error,
        { variant: 'error' },
      )
    }
  }

  useEffect(() => {
    if (dataState) {
      setPayload({
        ...dataState,
        attachments: (() => {
          try {
            const parsed = JSON.parse(dataState.attachments)
            if (Array.isArray(parsed)) {
              return parsed.map((url, idx) => ({
                id: Date.now() + idx,
                fileUrl: url,
              }))
            }
            return []
          } catch {
            return []
          }
        })(),
      })

      setFileNames(() => {
        try {
          const parsed = JSON.parse(dataState.attachments)
          if (Array.isArray(parsed)) {
            return parsed.map((url, idx) => ({
              id: Date.now() + idx,
              fileName: url.split('/').pop(),
            }))
          }
          return []
        } catch {
          return []
        }
      })
    }
  }, [dataState])

  return (
    <>
      <Container className="custom-container d-flex flex-column gap-1 py-2">
        <div className="pt-3">
          <BreadCrumbs
            breadCrumbActive={`${dataState ? 'Edit' : 'Tambah'} Event`}
            breadCrumbParent={'Event'}
            breadCrumbParentLink={
              userId ? `${URL_EVENT}/userId=${userId}` : URL_EVENT
            }
          />
        </div>
        <Card className="border-0 shadow-sm">
          <CardBody>
            <CardText tag="h5" className="pb-4">
              {dataState ? 'Edit' : 'Tambah'} Event
            </CardText>
            <FormGroup>
              <Label>Event Terkait (opsional)</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resEvent?.data}
                value={
                  resEvent?.data?.find(
                    (option) =>
                      String(option.value) === String(payload?.parentEventId),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    parentEventId: e?.value,
                  }))
                }}
                isClearable
                testId="event-form-parent-event"
              />
            </FormGroup>
            <FormGroup>
              <Label>Judul</Label>
              <Input
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    title: e.target?.value,
                  }))
                }}
                value={payload?.title}
                id="title"
                name="title"
                placeholder="Masukkan judul"
                data-testid="event-form-title"
              />
            </FormGroup>
            <FormGroup>
              <Label>Tanggal Event</Label>
              <div className="d-flex align-items-center justify-content-center border rounded">
                <Calendar size={17} className="ms-3" />
                <Flatpickr
                  value={payload?.eventDate}
                  placeholder="Pilih tanggal event"
                  data-testid="event-form-event-date"
                  options={{
                    locale: Indonesian,
                    enableTime: true,
                    time_24hr: true,
                    dateFormat: 'd-M-Y H:i',
                    onClose: (selectedDate, dateStr, instance) => {
                      setPayload((prev) => ({
                        ...prev,
                        eventDate: selectedDate,
                      }))
                    },
                  }}
                  className="form-control flat-picker bg-transparent border-0 shadow-none"
                />
                {payload?.eventDate && (
                  <div
                    className="px-2 cursor-pointer text-muted"
                    onClick={() =>
                      setPayload((prev) => ({
                        ...prev,
                        eventDate: null,
                      }))
                    }
                  >
                    <X size={15} />
                  </div>
                )}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Tanggal Kedaluwarsa (opsional)</Label>
              <div className="d-flex align-items-center border rounded">
                <Calendar size={17} className="ms-3" />
                <Flatpickr
                  options={{
                    locale: Indonesian,
                    enableTime: true,
                    time_24hr: true,
                    dateFormat: 'd-M-Y H:i',
                    defaultDate: payload?.expiryDate || null,
                    onClose: (selectedDate) => {
                      setPayload((prev) => ({
                        ...prev,
                        expiryDate: selectedDate,
                      }))
                    },
                  }}
                  placeholder="Pilih tanggal kedaluwarsa"
                  value={payload?.expiryDate || ''}
                  className="form-control flat-picker bg-transparent border-0 shadow-none flex-grow-1"
                  data-testid="event-form-expiry-date"
                />

                {payload?.expiryDate && (
                  <div
                    className="px-2 cursor-pointer text-muted"
                    onClick={() =>
                      setPayload((prev) => ({
                        ...prev,
                        expiryDate: null,
                      }))
                    }
                  >
                    <X size={15} />
                  </div>
                )}
              </div>
            </FormGroup>

            {/* <FormGroup>
              <Label>Event Partner</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resPartner?.data}
                value={
                  resPartner?.data?.find(
                    (option) =>
                      String(option.value) === String(payload?.partnerId),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    partnerId: e?.value,
                  }))
                }}
                isClearable
              />
            </FormGroup> */}

            <FormGroup>
              <Label>Tipe</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resUtils?.data?.eventType}
                value={
                  resUtils?.data?.eventType?.find(
                    (option) =>
                      String(option.value) === String(payload?.eventTypeId),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    eventTypeId: e?.value,
                  }))
                }}
                isClearable
                testId="event-form-type"
              />
            </FormGroup>

            <FormGroup>
              <Label>Kategori</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resUtils?.data?.eventCategory}
                value={
                  resUtils?.data?.eventCategory?.find(
                    (option) =>
                      String(option.value) === String(payload?.categoryId),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    categoryId: e?.value,
                  }))
                }}
                isClearable
                testId="event-form-category"
              />
            </FormGroup>

            <FormGroup>
              <Label>Objek Tipe</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resUtils?.data?.objectType}
                value={
                  resUtils?.data?.objectType?.find(
                    (option) =>
                      String(option.value) === String(payload?.objectType),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    objectType: e?.value,
                  }))
                }}
                isClearable
                testId="event-form-object-type"
              />
            </FormGroup>
            {/*
            <FormGroup>
              <Label>Status</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={resUtils?.eventStatus}
                value={
                  resUtils?.eventStatus.find(
                    (option) =>
                      String(option.value) === String(payload?.status),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    status: e?.value,
                  }))
                }}
                isClearable
              />
            </FormGroup> */}

            {/* <FormGroup>
              <Label>Pemateri</Label>
              <PMBSelect
                style={{ width: '100%' }}
                options={usersData?.newData}
                value={
                  usersData?.newData?.find(
                    (option) =>
                      String(option.value) === String(payload?.performeByUser),
                  ) ?? null
                }
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    performeByUser: e?.value,
                  }))
                }}
                isClearable
              />
            </FormGroup>

            <FormGroup>
              <Label>Pemateri</Label>
              <Input
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    performeByUser: e.target?.value,
                  }))
                }}
                value={payload?.performeByUser}
                id="performeByUser"
                name="performeByUser"
                placeholder="Masukkan pemateri"
              />
            </FormGroup>

            <FormGroup>
              <Label>Email Pemateri</Label>
              <Input
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    performeByUserEmail: e.target?.value,
                  }))
                }}
                value={payload?.performeByUserEmail}
                id="performeByUserEmail"
                name="performeByUserEmail"
                placeholder="Masukkan email pemateri"
              />
            </FormGroup> */}

            <FormGroup>
              <Label>Lokasi</Label>
              <Input
                type="textarea"
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    location: e.target?.value,
                  }))
                }}
                value={payload?.location}
                id="location"
                name="location"
                placeholder="Masukkan lokasi"
                data-testid="event-form-location"
              />
            </FormGroup>
            {/* <FormGroup>
              <Label>Kedaluwarsa Sertifikasi (opsional) </Label>
              <div className="d-flex align-items-center justify-content-center border rounded">
                <Calendar size={17} className="ms-3" />
                <Flatpickr
                  value={payload?.certificateExpireDate || ''}
                  placeholder="Pilih tanggal kedaluwarsa sertifikasi"
                  options={{
                    locale: Indonesian,
                    dateFormat: 'd-M-Y',
                    onClose: (selectedDate, dateStr, instance) => {
                      setPayload((prev) => ({
                        ...prev,
                        certificateExpireDate: selectedDate,
                      }))
                    },
                  }}
                  className="form-control flat-picker bg-transparent border-0 shadow-none"
                />
                {payload?.certificateExpireDate && (
                  <div
                    className="px-2 cursor-pointer text-muted"
                    onClick={() =>
                      setPayload((prev) => ({
                        ...prev,
                        certificateExpireDate: null,
                      }))
                    }
                  >
                    <X size={15} />
                  </div>
                )}
              </div>
            </FormGroup> */}
            <FormGroup>
              <Label>Deskripsi (opsional)</Label>
              <Input
                type="textarea"
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    description: e.target?.value,
                  }))
                }}
                value={payload?.description}
                id="description"
                name="description"
                placeholder="Masukkan deskripsi"
                data-testid="event-form-description"
              />
            </FormGroup>

            <FormGroup>
              <Label>Lampiran (opsional)</Label>
              <Form
                encType="multipart/form-data"
                className="flex-centered border rounded"
                style={{ overflow: 'hidden' }}
              >
                <fieldset className="fieldset">
                  <input
                    className="input-file"
                    id="transactions"
                    disabled={isLoading}
                    name="transactions"
                    data-testid="event-form-attachment"
                    onChange={async (e) => {
                      setIsLoading(true)
                      try {
                        const newFiles = e.target.files
                        let newFileNames = []
                        let newUploadedFiles = []

                        for (let i = 0; i < newFiles.length; i++) {
                          const uploadedFile = await uploadFile(
                            newFiles[i],
                            () => {},
                            'FILE',
                            'EVENT',
                            true,
                          )

                          if (!uploadedFile?.url) {
                            enqueueSnackbar(
                              uploadedFile?.payload || 'Upload gagal',
                              {
                                variant: 'error',
                              },
                            )
                          } else {
                            const fileId = Date.now() + i
                            newFileNames.push({
                              id: fileId,
                              fileName: newFiles[i].name,
                            })
                            newUploadedFiles.push({
                              id: fileId,
                              fileUrl: uploadedFile?.url,
                            })
                          }
                        }

                        setFileNames((prev) => [...prev, ...newFileNames])
                        setPayload((prev) => ({
                          ...prev,
                          attachments: [
                            ...(prev.attachments || []),
                            ...newUploadedFiles,
                          ],
                        }))
                      } catch (err) {
                        enqueueSnackbar('Terjadi kesalahan saat upload file', {
                          variant: 'error',
                        })
                      } finally {
                        setIsLoading(false)
                      }
                    }}
                    type="file"
                    accept=".png, .jpeg, .jpg, .pdf"
                    multiple
                  />
                  <label htmlFor="transactions" className="d-flex">
                    {isLoading ? <LoadingAnimation /> : 'Upload'}
                  </label>
                </fieldset>
                <div className="w-100" style={{ padding: '10px 14px' }}>
                  {fileNames?.length > 0 && !isLoading
                    ? fileNames?.map((e, i) => (
                        <span className="ms-3 badge-line" key={i}>
                          {e.fileName}
                          <X
                            size={16}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              const filteredFileNames = fileNames.filter(
                                (file) => file.id !== e.id,
                              )
                              const filteredFiles =
                                payload?.attachments?.filter(
                                  (file) => file.id !== e.id,
                                )
                              setFileNames(filteredFileNames)
                              setPayload((prev) => ({
                                ...prev,
                                attachments: filteredFiles,
                              }))
                            }}
                          />
                        </span>
                      ))
                    : 'Belum ada file'}
                </div>
              </Form>
            </FormGroup>

            {/* <FormGroup>
              <Input
                type="checkbox"
                onChange={(e) => {
                  const checked = e.target.checked

                  setPayload((prev) => {
                    if (!checked) {
                      return {
                        ...prev,
                        verified: false,
                        verifiedBy: null,
                        verifiedInstitution: null,
                      }
                    }

                    return {
                      ...prev,
                      verified: true,
                    }
                  })
                }}
                checked={payload?.verified || false}
              />{' '}
              Event Terverifikasi (opsional)
            </FormGroup>

            {payload?.verified && (
              <>
                <FormGroup>
                  <Label>Terverifikasi oleh (opsional)</Label>
                  <Input
                    onChange={(e) => {
                      setPayload((prev) => ({
                        ...prev,
                        verifiedBy: e.target?.value,
                      }))
                    }}
                    value={payload?.verifiedBy}
                    id="verifiedBy"
                    name="verifiedBy"
                    placeholder="Masukkan terverifikasi oleh"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Institusi terverifikasi (opsional)</Label>
                  <Input
                    onChange={(e) => {
                      setPayload((prev) => ({
                        ...prev,
                        verifiedInstitution: e.target?.value,
                      }))
                    }}
                    value={payload?.verifiedInstitution}
                    id="verifiedInstitution"
                    name="verifiedInstitution"
                    placeholder="Masukkan institusi terverifikasi"
                  />
                </FormGroup>
              </>
            )} */}

            <div className="d-flex gap-3">
              <CustomButton
                onClick={() =>
                  navigate(userId ? `${URL_EVENT}/userId=${userId}` : URL_EVENT)
                }
                outline
                className="btn-cancel"
                block
                testId="event-form-cancel-button"
              >
                Batal
              </CustomButton>
              <CustomButton
                onClick={() => handleSubmit()}
                disabled={
                  isLoading ||
                  !payload?.title ||
                  !payload?.eventDate ||
                  !payload?.eventTypeId ||
                  !payload?.objectType ||
                  !payload?.categoryId
                }
                block
                color="primary"
                testId="event-form-submit-button"
              >
                Simpan
              </CustomButton>
            </div>
          </CardBody>
        </Card>
      </Container>
    </>
  )
}

export default FormEvent
